import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, FolderTree, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { resolveMenu, pruneMenu, findNode, groupSlug, pageExcerpt, serializeMenu, locateNode } from '../lib/menu';
import { GroupOverviewEditor } from '../components/GroupOverviewEditor';

// Auto-generated landing page for a menu group: lists its pages and sub-groups
// as cards, showing only what the current viewer may access. Admins right-click
// the banner / intro to edit them in place.
export default function MenuGroupOverview() {
  const { slug } = useParams();
  const { settings, customPages, loaded, canAccess, isAdmin, mediaUrl, updateSettings } = useAuth();
  const [editing, setEditing] = useState(false);

  const resolved = resolveMenu({
    customPages,
    menuTree: (settings && settings.menuTree) || [],
    customGroups: (settings && settings.customGroups) || [],
  });
  const group = findNode(resolved, (n) => n.kind === 'group' && groupSlug(n.id) === slug);

  if (!group) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center fade-in" data-testid="group-overview-missing">
        <h1 className="font-title text-2xl" style={{ color: 'var(--maroon)' }}>{loaded ? 'Menu group not found' : 'Loading\u2026'}</h1>
      </div>
    );
  }

  const canEdit = isAdmin || canAccess('admin-pages');
  const nowMs = Date.now();
  const isLive = (c) => !c.draft || (c.publishAt && new Date(c.publishAt).getTime() <= nowMs);
  const showPage = (c) => canAccess(c.pageKey) && (isLive(c) || isAdmin);
  const items = pruneMenu(group.children, showPage);
  const pageByKey = (k) => (customPages || []).find((p) => p.page_key === k);
  const introParas = (group.intro || '').split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);

  const onContext = (e) => {
    if (!canEdit) return;
    e.preventDefault();
    setEditing(true);
  };
  const saveInline = async (patch) => {
    const tree = JSON.parse(JSON.stringify(resolved));
    const loc = locateNode(tree, group.id);
    Object.assign(loc.node, patch);
    await updateSettings({ ...settings, menuTree: serializeMenu(tree) });
    toast.success('Overview page updated.');
  };
  const editStyle = canEdit ? { cursor: 'context-menu', outline: '1px dashed rgba(138,10,8,0.35)', outlineOffset: 4, borderRadius: 2 } : undefined;

  return (
    <div className="fade-in" data-testid="group-overview">
      {group.headerImage && (
        <div
          className="relative w-full h-56 sm:h-72 overflow-hidden"
          data-testid="group-overview-header-image"
          onContextMenu={onContext}
          style={canEdit ? { cursor: 'context-menu' } : undefined}
          title={canEdit ? 'Right-click to edit the header image' : undefined}
        >
          <img src={mediaUrl(group.headerImage)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(20,30,50,0.75), rgba(20,30,50,0.1))' }} />
          <div className="absolute bottom-0 left-0 right-0">
            <div className="max-w-6xl mx-auto px-4 pb-6">
              <h1 className="font-title text-4xl sm:text-5xl" style={{ color: 'var(--cream)' }} data-testid="group-overview-title">{group.label}</h1>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-6xl mx-auto px-4 py-10">
        {!group.headerImage && (
          <h1 className="font-title text-4xl sm:text-5xl mb-2" style={{ color: 'var(--navy)' }} data-testid="group-overview-title">{group.label}</h1>
        )}
        {introParas.length > 0 ? (
          <div
            className="max-w-3xl mb-8 space-y-3"
            data-testid="group-overview-intro"
            onContextMenu={onContext}
            style={editStyle}
            title={canEdit ? 'Right-click to edit the introduction' : undefined}
          >
            {introParas.map((p, i) => <p key={i} className="text-base leading-relaxed" style={{ color: '#2b2b2b' }}>{p}</p>)}
          </div>
        ) : (
          <p className="text-base mb-8" style={{ color: 'var(--olive)' }}>
            {items.length ? 'Choose a page below.' : 'There is nothing in this section you can view yet.'}
          </p>
        )}
        {introParas.length > 0 && !items.length && (
          <p className="text-base mb-8" style={{ color: 'var(--olive)' }}>There is nothing in this section you can view yet.</p>
        )}
        {canEdit && (!introParas.length || !group.headerImage) && (
          <button
            type="button"
            data-testid="group-overview-edit-btn"
            onClick={() => setEditing(true)}
            onContextMenu={onContext}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border mb-8"
            style={{ borderColor: 'rgba(138,10,8,0.35)', color: 'var(--maroon)', background: 'white' }}
          >
            <Pencil size={13} /> {introParas.length ? 'Add a header image' : group.headerImage ? 'Add an introduction' : 'Add an introduction & header image'}
          </button>
        )}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="group-overview-cards">
          {items.map((n) => (n.kind === 'group' ? <GroupCard key={n.id} node={n} /> : <PageCard key={n.id} node={n} page={pageByKey(n.pageKey)} />))}
        </div>
      </div>
      {editing && (
        <GroupOverviewEditor key={group.id} node={group} open onClose={() => setEditing(false)} onApply={saveInline} applyLabel="Save" hint={false} />
      )}
    </div>
  );
}

function PageCard({ node, page }) {
  const excerpt = pageExcerpt(page);
  return (
    <Link
      to={node.path}
      data-testid={`group-overview-card-${node.id}`}
      className="group block rounded-lg border bg-white p-5 shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md"
      style={{ borderColor: 'rgba(0,0,0,0.1)' }}
    >
      <h2 className="font-title text-lg mb-1" style={{ color: 'var(--navy)' }}>{node.label}</h2>
      {excerpt && <p className="text-sm mb-3" style={{ color: '#4b4b4b' }}>{excerpt}</p>}
      <span className="inline-flex items-center gap-1 text-sm font-title" style={{ color: 'var(--maroon)' }}>
        Open <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}

function GroupCard({ node }) {
  const inner = (
    <>
      <h2 className="font-title text-lg mb-1 flex items-center gap-2" style={{ color: 'var(--navy)' }}><FolderTree size={16} /> {node.label}</h2>
      <ul className="text-sm space-y-1" style={{ color: '#4b4b4b' }}>
        {node.children.slice(0, 5).map((c) => (
          <li key={c.id}>
            {c.kind === 'page' ? <Link to={c.path} className="hover:underline">{c.label}</Link> : (c.landingPath ? <Link to={c.landingPath} className="hover:underline">{c.label}</Link> : c.label)}
          </li>
        ))}
        {node.children.length > 5 && <li>…and {node.children.length - 5} more</li>}
      </ul>
    </>
  );
  const cls = 'block rounded-lg border p-5 shadow-sm';
  const style = { borderColor: 'rgba(0,0,0,0.1)', background: 'var(--cream)' };
  return node.landingPath
    ? <Link to={node.landingPath} data-testid={`group-overview-card-${node.id}`} className={`${cls} transition-transform hover:-translate-y-0.5 hover:shadow-md`} style={style}>{inner}</Link>
    : <div data-testid={`group-overview-card-${node.id}`} className={cls} style={style}>{inner}</div>;
}
